"use client";

import Chevron from "@homeinstead/icons/Chevron";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import Link from "next/link";
import * as React from "react";
import { cn } from "../../../utils/classnames";

const NavigationMenuListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, children, ...props }, ref) => {
  return (
    <li className="overflow-hidden xl:last:rounded-b-sm xl:first:rounded-t-sm">
      <Link
        className={cn(
          "block w-full select-none space-y-1 p-3 text-new-beginnings-base leading-none no-underline outline-none transition-colors hover:bg-new-beginnings-base hover:text-support-tints-400 focus:bg-new-beginnings-base focus:text-white xl:hover:text-white",
          className,
        )}
        href="/"
        ref={ref}
        {...props}
      >
        <NavigationMenuLink asChild>
          <div>
            <p className="line-clamp-2 font-semibold text-base xl:text-sm">
              {children}
            </p>
          </div>
        </NavigationMenuLink>
      </Link>
    </li>
  );
});
NavigationMenuListItem.displayName = "NavigationMenuListItem";

interface NavigationMenuTriggerProps
  extends React.ComponentPropsWithoutRef<
    typeof NavigationMenuPrimitive.Trigger
  > {
  hasChevron?: boolean;
}

const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  NavigationMenuTriggerProps
>(({ className, children, hasChevron = true, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.Trigger
      className={cn(
        "group inline-flex h-12 w-max items-center justify-center rounded-sm py-2 font-medium transition-colors",
        "disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-new-beginnings-base/10 xl:data-[state=open]:bg-new-beginnings-base/10",
        // Default bar is dark green — light focus ring. Campaign (white shell)
        // needs a green ring (light ring is near-invisible on white).
        "focus:xl:outline-support-tints-400 group-data-[appearance=campaign]/navbar:focus:xl:outline-new-beginnings-base",
        hasChevron ? "px-4" : "px-0 xl:px-4",
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}{" "}
      {hasChevron && (
        <Chevron
          aria-hidden="true"
          className={cn(
            "relative top-[1px] ml-1 h-3 w-3 fill-new-beginnings-base transition duration-200 group-data-[state=open]:rotate-180",
            "group-data-[appearance=default]/navbar:xl:fill-support-tints-400",
          )}
        />
      )}
    </NavigationMenuPrimitive.Trigger>
  );
});
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.List
    className={cn(
      "group relative flex flex-1 list-none items-center justify-between space-x-1",
      className,
    )}
    ref={ref}
    {...props}
  />
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuLink = NavigationMenuPrimitive.Link;

const NavigationMenuItem = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Item
    className={cn("relative", className)}
    ref={ref}
    {...props}
  >
    {children}
  </NavigationMenuPrimitive.Item>
));
NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName;

const NavigationMenuIndicator = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Indicator
    className={cn(
      "data-[state=hidden]:fade-out data-[state=visible]:fade-in top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=hidden]:animate-out data-[state=visible]:animate-in",
      className,
    )}
    ref={ref}
    {...props}
  >
    <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
  </NavigationMenuPrimitive.Indicator>
));
NavigationMenuIndicator.displayName =
  NavigationMenuPrimitive.Indicator.displayName;

const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    className={cn(
      "right:0 top-full left-auto mt-2 min-w-[200px] border-l outline-new-beginnings-base xl:absolute xl:rounded-sm xl:border",
      className,
    )}
    ref={ref}
    {...props}
  />
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

export {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuListItem,
  NavigationMenuPrimitive,
  NavigationMenuTrigger,
};
